import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { GraduationCap, Lock, Unlock, ChevronRight, BarChart3, BookOpen, Brain, ArrowLeft, Trophy, Target, TrendingUp, TrendingDown, Minus, History, CreditCard, CheckCircle, AlertTriangle, ChevronDown, FileText, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import ExaminationEngine from './ExaminationEngine';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
};

const PromotionPreparation = () => {
  const [ranks, setRanks] = useState([]);
  const [profile, setProfile] = useState(null);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectingRank, setSelectingRank] = useState(false);
  const [examMode, setExamMode] = useState(null); // { year, rankId, rankName }
  const [history, setHistory] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [subscribeDialog, setSubscribeDialog] = useState(false);
  const [subStatus, setSubStatus] = useState(null);
  const [paying, setPaying] = useState(false);
  const [weakAreas, setWeakAreas] = useState([]);
  const [reviewingSession, setReviewingSession] = useState(null);
  const [startingMock, setStartingMock] = useState(false);
  const [aiQuestions, setAiQuestions] = useState(null);
  const [generatingAi, setGeneratingAi] = useState(false);
  const [resources, setResources] = useState([]);
  const [resourceCategories, setResourceCategories] = useState([]);
  const [expandedResource, setExpandedResource] = useState(null);

  const fetchRanks = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/promotion/ranks`, { headers: getAuthHeaders() });
      if (res.ok) setRanks(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/promotion/my-profile`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
        if (data.rank_id) {
          const yRes = await fetch(`${API_BASE}/api/promotion/years/${data.rank_id}`, { headers: getAuthHeaders() });
          if (yRes.ok) setYears(await yRes.json());
        }
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchRanks();
      await fetchProfile();
      setLoading(false);
    };
    load();
  }, [fetchRanks, fetchProfile]);

  const fetchHistory = useCallback(async (rankId) => {
    try {
      const url = rankId ? `${API_BASE}/api/promotion/exams/history?rank_id=${rankId}` : `${API_BASE}/api/promotion/exams/history`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) setHistory(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchPerformance = useCallback(async (rankId) => {
    try {
      const url = rankId ? `${API_BASE}/api/promotion/exams/performance?rank_id=${rankId}` : `${API_BASE}/api/promotion/exams/performance`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) setPerformance(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchWeakAreas = async (rankId) => {
    try {
      const url = rankId ? `${API_BASE}/api/promotion/my-weak-areas?rank_id=${rankId}` : `${API_BASE}/api/promotion/my-weak-areas`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) setWeakAreas(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchResources = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/promotion/resources`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setResources(data.resources || []);
        setResourceCategories(data.categories || []);
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    if (profile?.rank_id) {
      fetchHistory(profile.rank_id);
      fetchPerformance(profile.rank_id);
      fetchWeakAreas(profile.rank_id);
      fetchResources();
    }
  }, [profile, fetchHistory, fetchPerformance, fetchResources]);


  const openHistoryReview = async (sessionId) => {
    try {
      const res = await fetch(`${API_BASE}/api/promotion/exams/${sessionId}/review-from-history`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setReviewingSession(data);
      } else { toast.error('Failed to load review'); }
    } catch (e) { toast.error('Error loading review'); }
  };

  const startMockExam = async () => {
    setStartingMock(true);
    try {
      const res = await fetch(`${API_BASE}/api/promotion/mock/generate`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ rank_id: profile.rank_id })
      });
      if (res.ok) {
        const data = await res.json();
        setExamMode({
          year: 0, rankId: profile.rank_id, rankName: profile.rank_name,
          mockSession: data.session, mockQuestions: data.questions
        });
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to generate mock exam');
      }
    } catch (e) { toast.error('Error starting mock exam'); }
    setStartingMock(false);
  };

  const generateAiQuestions = async () => {
    setGeneratingAi(true);
    try {
      const res = await fetch(`${API_BASE}/api/promotion/mock/ai-questions`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ rank_id: profile.rank_id, num_questions: 10 })
      });
      if (res.ok) {
        const data = await res.json();
        setAiQuestions(data.questions);
        toast.success(`${data.count} likely-to-come questions generated!`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to generate questions');
      }
    } catch (e) { toast.error('AI generation error'); }
    setGeneratingAi(false);
  };

  const fetchSubStatus = useCallback(async () => {
    try {
      let url = `${API_BASE}/api/promotion/subscription/status`;
      if (profile?.rank_id) url += `?rank_id=${profile.rank_id}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) setSubStatus(await res.json());
    } catch (e) { console.error(e); }
  }, [profile?.rank_id]);

  useEffect(() => { fetchSubStatus(); }, [fetchSubStatus]);

  // Check URL params for payment callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('promotion_payment');
    if (paymentStatus === 'success') {
      toast.success('Payment successful! Premium content unlocked.');
      fetchSubStatus();
      fetchProfile();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (paymentStatus === 'failed') {
      toast.error('Payment was not completed. Please try again.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleSubscribe = async () => {
    setPaying(true);
    try {
      const res = await fetch(`${API_BASE}/api/promotion/subscription/initialize`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ rank_id: profile?.rank_id })
      });
      if (res.ok) {
        const data = await res.json();
        // Redirect to Paystack
        window.location.href = data.authorization_url;
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to initialize payment');
      }
    } catch (e) { toast.error('Payment error'); }
    setPaying(false);
  };

  const selectRank = async (rankId) => {
    try {
      const res = await fetch(`${API_BASE}/api/promotion/select-rank`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ rank_id: rankId })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Rank selected: ${data.rank_name}`);
        setSelectingRank(false);
        await fetchProfile();
        const yRes = await fetch(`${API_BASE}/api/promotion/years/${rankId}`, { headers: getAuthHeaders() });
        if (yRes.ok) setYears(await yRes.json());
      }
    } catch (e) { toast.error('Failed to select rank'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-amber-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // No rank selected — show rank selection
  if (!profile?.rank_id || selectingRank) {
    return (
      <div className="space-y-6">
        {/* Welcome Header */}
        <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                <GraduationCap className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-amber-800">Promotion Exams Preparation</h2>
                <p className="text-sm text-amber-600 mt-1">
                  Prepare for your GES promotion examination with authentic past questions, timed practice tests and AI-generated mock examinations.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rank Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="h-5 w-5 text-amber-600" />
              Select Your Aspiring Rank
            </CardTitle>
            <CardDescription>Choose the promotion rank you are preparing for</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {ranks.map((rank, i) => (
                <button
                  key={rank.id}
                  onClick={() => selectRank(rank.id)}
                  className="p-4 border-2 rounded-xl text-left hover:border-amber-400 hover:bg-amber-50 transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold group-hover:bg-amber-200 transition-colors">
                      {i + 1}
                    </span>
                    <div>
                      <p className="font-semibold text-gray-900">{rank.name}</p>
                      <p className="text-xs text-gray-500">{rank.description || `${rank.exam_duration_minutes} min exam`}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            {ranks.length === 0 && (
              <p className="text-center text-gray-500 py-8">No promotion ranks configured yet. Please check back later.</p>
            )}
          </CardContent>
        </Card>

        {selectingRank && profile?.rank_id && (
          <div className="text-center">
            <Button variant="outline" onClick={() => setSelectingRank(false)}>
              <ArrowLeft className="h-4 w-4 mr-1" />Back to Dashboard
            </Button>
          </div>
        )}
      </div>
    );
  }

  // If in exam mode, render the exam engine
  if (examMode) {
    return (
      <ExaminationEngine
        rankId={examMode.rankId}
        year={examMode.year}
        rankName={examMode.rankName}
        existingSession={examMode.mockSession || null}
        existingQuestions={examMode.mockQuestions || null}
        onBack={() => {
          setExamMode(null);
          fetchProfile();
          fetchHistory(profile?.rank_id);
          fetchPerformance(profile?.rank_id);
          fetchWeakAreas(profile?.rank_id);
        }}
      />
    );
  }

  const trendIcon = performance?.trend === 'improving' ? <TrendingUp className="h-4 w-4 text-green-600" /> :
                    performance?.trend === 'declining' ? <TrendingDown className="h-4 w-4 text-red-500" /> :
                    <Minus className="h-4 w-4 text-gray-400" />;

  // Rank selected — show promotion dashboard
  return (
    <div className="space-y-6">
      {/* Header with selected rank */}
      <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                <GraduationCap className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-amber-800">Promotion Exam Preparation</h2>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-amber-600">Aspiring rank: <span className="font-semibold">{profile.rank_name}</span></p>
                  {subStatus?.has_rank_subscription && (
                    <Badge className="bg-green-100 text-green-700 border-green-300" data-testid="subscribed-badge">Subscribed</Badge>
                  )}
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setSelectingRank(true)} className="border-amber-300 text-amber-700 hover:bg-amber-100">
              Change Rank
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Mobile scroll indicator */}
      <div className="sm:hidden flex items-center justify-center gap-2 text-gray-600 animate-bounce py-1" data-testid="scroll-indicator">
        <ChevronsUpDown className="h-5 w-5" />
        <span className="text-xs">Scroll for more content</span>
      </div>

      {/* Standalone Subscribe Button — show only if not subscribed to this rank */}
      {!subStatus?.has_rank_subscription && (
        <Card className="border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50" data-testid="subscribe-card">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-200 rounded-full flex items-center justify-center shrink-0">
                <CreditCard className="h-6 w-6 text-amber-700" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-amber-900 mb-1">Subscribe to {profile?.rank_name || 'Premium'}</h3>
                <p className="text-sm text-amber-700 mb-3">
                  Subscribe to unlock all past questions for <strong>{profile?.rank_name}</strong> and access learning resource materials including education policies, teaching standards, and aptitude test tips.
                </p>
                <Button onClick={() => setSubscribeDialog(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="subscribe-btn">
                  <CreditCard className="h-4 w-4 mr-2" />
                  Subscribe Now — {subStatus?.currency || 'GHS'} {subStatus?.price || '0'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Learning Resource Materials */}
      <Card className="border-teal-200">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-5 w-5 text-teal-600" />
            Learning Resource Materials
          </CardTitle>
          <CardDescription>
            {subStatus?.has_subscription
              ? 'Click to expand each topic for study materials'
              : 'Subscribe to access learning resource materials'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {resourceCategories.map(cat => {
              const catResources = resources.filter(r => r.category === cat.id);
              const isExpanded = expandedResource === cat.id;
              return (
                <div key={cat.id} className="border rounded-xl overflow-hidden">
                  <button
                    onClick={() => {
                      if (!subStatus?.has_subscription) { setSubscribeDialog(true); return; }
                      setExpandedResource(isExpanded ? null : cat.id);
                    }}
                    className={`w-full flex items-center justify-between p-3 sm:p-4 text-left transition-colors ${
                      subStatus?.has_subscription ? 'hover:bg-teal-50' : 'opacity-75'
                    }`}
                    data-testid={`resource-category-${cat.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {subStatus?.has_subscription ? (
                        <FileText className="h-5 w-5 text-teal-600" />
                      ) : (
                        <Lock className="h-5 w-5 text-gray-400" />
                      )}
                      <span className="font-semibold text-gray-900 text-sm">{cat.name}</span>
                      {catResources.length > 0 && (
                        <Badge variant="outline" className="text-xs">{catResources.length}</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {!subStatus?.has_subscription && (
                        <Badge className="bg-amber-100 text-amber-700 border-amber-300" variant="outline">Premium</Badge>
                      )}
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </button>
                  {isExpanded && subStatus?.has_subscription && (
                    <div className="border-t bg-gray-50 p-3 sm:p-4 space-y-3" data-testid={`resource-content-${cat.id}`}>
                      {catResources.length > 0 ? catResources.map(res => (
                        <div key={res.id} className="bg-white rounded-lg p-3 border">
                          <h4 className="font-semibold text-sm text-teal-800 mb-2">{res.title}</h4>
                          <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{res.content}</div>
                        </div>
                      )) : (
                        <p className="text-sm text-gray-500 text-center py-4">No materials available yet for this topic.</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {resourceCategories.length === 0 && (
              <p className="text-center text-gray-500 py-4">Learning resources coming soon.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Past Questions Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-blue-600" />
            Past Questions
          </CardTitle>
          <CardDescription>Practice with authentic past examination questions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {years.length > 0 ? years.map(yr => (
              <div
                key={yr.year}
                data-testid={`year-row-${yr.year}`}
                className={`flex items-center justify-between p-3 sm:p-4 border rounded-xl transition-all ${
                  yr.accessible
                    ? 'border-green-200 bg-green-50 hover:border-green-400 cursor-pointer'
                    : 'border-gray-200 bg-gray-50'
                }`}
                onClick={() => {
                  if (yr.accessible) {
                    if (yr.question_count === 0) {
                      toast.info('No questions available for this year yet');
                      return;
                    }
                    setExamMode({ year: yr.year, rankId: profile.rank_id, rankName: profile.rank_name });
                  } else {
                    setSubscribeDialog(true);
                  }
                }}
              >
                <div className="flex items-center gap-3">
                  {yr.accessible ? (
                    <Unlock className="h-5 w-5 text-green-600" />
                  ) : (
                    <Lock className="h-5 w-5 text-gray-400" />
                  )}
                  <div>
                    <p className="font-semibold text-gray-900">{yr.year}</p>
                    <p className="text-xs text-gray-500">{yr.question_count} questions</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {yr.free ? (
                    <Badge className="bg-green-100 text-green-700 border-green-300" variant="outline">Free</Badge>
                  ) : yr.accessible ? (
                    <Badge className="bg-blue-100 text-blue-700 border-blue-300" variant="outline">Unlocked</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700 border-amber-300" variant="outline">Premium</Badge>
                  )}
                  {yr.accessible && yr.question_count > 0 && (
                    <span className="text-xs text-green-600 font-medium">Start</span>
                  )}
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            )) : (
              <p className="text-center text-gray-500 py-6">No past questions available for this rank yet.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* AI Practice Section */}
      <Card className="border-purple-200">
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center shrink-0">
              <Brain className="h-6 w-6 text-purple-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-purple-800">Real-Time Practice</h3>
              <p className="text-xs sm:text-sm text-purple-600">Intelligent question selection based on frequency analysis and your weak areas</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Mock Exam Button */}
            <button
              onClick={startMockExam}
              disabled={startingMock || !subStatus?.has_rank_subscription}
              data-testid="start-mock-exam-btn"
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                subStatus?.has_rank_subscription
                  ? 'border-purple-300 bg-purple-50 hover:border-purple-500 hover:shadow-md cursor-pointer'
                  : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <Target className="h-5 w-5 text-purple-600" />
                <span className="font-semibold text-gray-900 text-sm">Mock Examination</span>
              </div>
              <p className="text-xs text-gray-500">Timed exam with weighted questions from all years — prioritizes rare and frequently-missed questions</p>
              {startingMock && <p className="text-xs text-purple-600 mt-2 animate-pulse">Generating exam...</p>}
              {!subStatus?.has_rank_subscription && <p className="text-xs text-amber-600 mt-2">Requires subscription for this rank</p>}
            </button>

            {/* AI Questions Button */}
            <button
              onClick={generateAiQuestions}
              disabled={generatingAi || !subStatus?.has_rank_subscription}
              data-testid="generate-ai-questions-btn"
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                subStatus?.has_rank_subscription
                  ? 'border-indigo-300 bg-indigo-50 hover:border-indigo-500 hover:shadow-md cursor-pointer'
                  : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <Brain className="h-5 w-5 text-indigo-600" />
                <span className="font-semibold text-gray-900 text-sm">Likely to Come Questions</span>
              </div>
              <p className="text-xs text-gray-500">AI analyzes past exam patterns to predict questions likely to appear in your next promotion exam</p>
              {generatingAi && <p className="text-xs text-indigo-600 mt-2 animate-pulse">AI is analyzing patterns...</p>}
              {!subStatus?.has_rank_subscription && <p className="text-xs text-amber-600 mt-2">Requires subscription for this rank</p>}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* AI Generated Questions Display */}
      {aiQuestions && aiQuestions.length > 0 && (
        <Card className="border-indigo-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Brain className="h-5 w-5 text-indigo-600" />
              Likely to Come Questions
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => setAiQuestions(null)} className="text-xs">
              Close
            </Button>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-gray-500 mb-3">AI-predicted questions based on past exam frequency analysis. Use these to focus your revision.</p>
            <div className="space-y-3">
              {aiQuestions.map((q, i) => (
                <AiQuestionCard key={q.id || i} question={q} index={i} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}


      {/* Performance Summary */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-green-600" />
            My Performance
          </CardTitle>
          {history.length > 0 && (
            <Button variant="outline" size="sm" data-testid="history-toggle-btn" onClick={() => setShowHistory(!showHistory)}>
              <History className="h-4 w-4 mr-1" />{showHistory ? 'Hide' : 'History'}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {performance && performance.attempts > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center" data-testid="performance-stats">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-700" data-testid="perf-average">{performance.average_score}%</p>
                <p className="text-xs text-gray-500">Average</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-700" data-testid="perf-best">{performance.best_score}%</p>
                <p className="text-xs text-gray-500">Best</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg">
                <p className="text-2xl font-bold text-amber-700" data-testid="perf-attempts">{performance.attempts}</p>
                <p className="text-xs text-gray-500">Attempts</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg flex flex-col items-center">
                <div className="flex items-center gap-1" data-testid="perf-trend">{trendIcon}<span className="text-sm font-bold capitalize">{performance.trend}</span></div>
                <p className="text-xs text-gray-500">Trend</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-400">--</p>
                <p className="text-xs text-gray-500">Average Score</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-400">--</p>
                <p className="text-xs text-gray-500">Best Score</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-amber-600">0</p>
                <p className="text-xs text-gray-500">Attempts</p>
              </div>
            </div>
          )}

          {/* Attempt History Table */}
          {showHistory && history.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-gray-500">
                    <th className="pb-2 pr-3">Year</th>
                    <th className="pb-2 pr-3">Score</th>
                    <th className="pb-2 pr-3">Correct</th>
                    <th className="pb-2 pr-3">Wrong</th>
                    <th className="pb-2">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, i) => (
                    <tr key={h.id || i} data-testid={`history-row-${i}`} className="border-b last:border-0 cursor-pointer hover:bg-gray-50" onClick={() => openHistoryReview(h.id)}>
                      <td className="py-2 pr-3 font-medium">{h.year === 0 ? 'Mock' : h.year}</td>
                      <td className="py-2 pr-3">
                        <Badge variant="outline" className={h.percentage >= 50 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}>{h.percentage}%</Badge>
                      </td>
                      <td className="py-2 pr-3 text-green-600">{h.correct_count}</td>
                      <td className="py-2 pr-3 text-red-500">{h.wrong_count}</td>
                      <td className="py-2 text-gray-500 text-xs">{new Date(h.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Weak Areas */}
      {weakAreas.length > 0 && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Areas to Improve
            </CardTitle>
            <CardDescription>Questions you frequently get wrong</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {weakAreas.slice(0, 5).map((q, i) => (
                <div key={q.question_id} className="p-3 bg-red-50 rounded-lg border border-red-100">
                  <p className="text-sm font-medium text-gray-800 line-clamp-2">{q.question_text}</p>
                  <div className="flex gap-3 mt-1 text-xs">
                    <span className="text-red-600">Wrong: {q.wrong}x</span>
                    <span className="text-green-600">Correct: {q.correct}x</span>
                    <span className="text-gray-500">Answer: {q.correct_answer}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* History Review Modal */}
      {reviewingSession && (
        <Dialog open={!!reviewingSession} onOpenChange={() => setReviewingSession(null)}>
          <DialogContent data-testid="history-review-modal" className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Review: {reviewingSession.rank_name} {reviewingSession.year}</DialogTitle>
              <DialogDescription>Score: {reviewingSession.percentage}% | Correct: {reviewingSession.correct_count} | Wrong: {reviewingSession.wrong_count}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              {reviewingSession.review?.map((q, i) => (
                <div key={q.question_id} data-testid={`history-review-q-${i}`} className={`p-3 border rounded-lg border-l-4 ${q.is_correct ? 'border-l-green-500' : q.is_unanswered ? 'border-l-gray-300' : 'border-l-red-500'}`}>
                  <p className="text-sm font-medium mb-2">Q{i+1}. {q.question_text}</p>
                  {/* Multi-choice / True-False options */}
                  {q.options && Object.keys(q.options).length > 0 && (
                    <div className="space-y-1">
                      {Object.entries(q.options).map(([key, val]) => {
                        const isCorrect = q.correct_answer === key;
                        const isUser = q.user_answer === key;
                        let cls = 'bg-gray-50 text-gray-600';
                        if (isCorrect) cls = 'bg-green-100 text-green-800 font-medium';
                        if (isUser && !isCorrect) cls = 'bg-red-100 text-red-800 line-through';
                        return <div key={key} className={`px-2 py-1 rounded text-xs ${cls}`}>{key}. {val}</div>;
                      })}
                    </div>
                  )}
                  {/* Fill-in-the-gap */}
                  {q.question_type === 'fill_in' && (
                    <div className="space-y-1">
                      <div className={`px-2 py-1 rounded text-xs ${q.is_correct ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        Your answer: <strong>{q.user_answer || '(blank)'}</strong>
                      </div>
                      {!q.is_correct && (
                        <div className="px-2 py-1 rounded text-xs bg-green-100 text-green-800">
                          Correct answer: <strong>{q.correct_answer}</strong>
                        </div>
                      )}
                    </div>
                  )}
                  {q.explanation && <p className="mt-1 text-xs text-blue-600 bg-blue-50 p-1 rounded">{q.explanation}</p>}
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Subscription Dialog */}
      <Dialog open={subscribeDialog} onOpenChange={setSubscribeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-amber-500" />
              Premium Subscription
            </DialogTitle>
            <DialogDescription>
              Subscribe to unlock all premium content.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3 space-y-3">
            <p className="text-sm text-gray-600">
              Subscribe to unlock all past questions and live exams for <strong>{profile?.rank_name || 'your selected rank'}</strong>, plus access to <strong>learning resource materials</strong> across all ranks.
            </p>
            <div className="flex items-center justify-between p-4 bg-amber-50 rounded-xl border border-amber-200">
              <div>
                <p className="font-semibold text-gray-900">Subscription Fee</p>
                <p className="text-xs text-gray-500">One-time payment, unlocks all premium years</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-amber-700">
                  {subStatus?.currency || 'GHS'} {subStatus?.price || '0.00'}
                </p>
              </div>
            </div>
            <ul className="text-xs text-gray-500 space-y-1">
              <li className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-green-500" />Access all premium past question years</li>
              <li className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-green-500" />Learning resource materials (policies, standards, tips)</li>
              <li className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-green-500" />Unlimited attempts per year</li>
              <li className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-green-500" />Full answer review with explanations</li>
              <li className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-green-500" />AI Mock Exams and Likely-to-Come Questions</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubscribeDialog(false)}>Cancel</Button>
            <Button onClick={handleSubscribe} disabled={paying || !subStatus?.price} className="bg-amber-600 hover:bg-amber-700">
              <CreditCard className="h-4 w-4 mr-2" />
              {paying ? 'Processing...' : `Pay ${subStatus?.currency || 'GHS'} ${subStatus?.price || '0'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Sub-component for displaying AI-generated questions
const AiQuestionCard = ({ question, index }) => {
  const [showAnswer, setShowAnswer] = useState(false);
  const q = question;
  const options = q.options || { A: q.option_a, B: q.option_b, C: q.option_c, D: q.option_d };

  return (
    <div className="p-3 border rounded-xl bg-white" data-testid={`ai-question-${index}`}>
      <p className="text-sm font-medium text-gray-800 mb-2">Q{index + 1}. {q.question_text}</p>
      <div className="space-y-1 mb-2">
        {Object.entries(options).map(([key, val]) => {
          const isCorrect = showAnswer && q.correct_option === key;
          return (
            <div key={key} className={`px-2 py-1.5 rounded text-xs ${isCorrect ? 'bg-green-100 text-green-800 font-medium' : 'bg-gray-50 text-gray-600'}`}>
              {key}. {val}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <button onClick={() => setShowAnswer(!showAnswer)} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">
          {showAnswer ? 'Hide Answer' : 'Show Answer'}
        </button>
        {q.topic && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{q.topic}</Badge>}
      </div>
      {showAnswer && q.explanation && (
        <p className="mt-1.5 text-xs text-blue-600 bg-blue-50 p-2 rounded">{q.explanation}</p>
      )}
    </div>
  );
};

export default React.memo(PromotionPreparation);
